// strona logowania - wariant Wise UI

import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BoxArrowInRight, PersonPlus } from 'react-bootstrap-icons';
import qs from 'query-string';
import classnames from 'classnames';

import { reduxActions, useDispatch } from 'store';
import { useNotifications } from 'hooks';
import { usePostAuthLogin } from 'api';
import { LoginFormSchema } from 'pages/Login/schema';
import { Input, FormElement, Button, Link, Checkbox } from 'components/controls';

import styles from 'theme/pages/wise/WiseLogin/WiseLogin.module.scss';

const { REACT_APP_OPEN_PROFILE_CLIENT_ID, REACT_APP_OPEN_PROFILE_CLIENT_SECRET } = process.env;

interface IProps {
  isPopover?: boolean;
}

const WiseLogin = ({ isPopover }: IProps) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSuccessMessage, showErrorMessage } = useNotifications();

  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem('rememberUser') ? true : false
  );

  const returnUrl = useMemo(
    () => String(qs.parseUrl(document.location.href).query.return_url || ''),
    []
  );

  const { mutate: login, isLoading: isLogging } = usePostAuthLogin({
    onSuccess: ({ access_token, refresh_token, session_id, message, status }) => {
      rememberMe
        ? localStorage.setItem('rememberUser', values.username)
        : localStorage.removeItem('rememberUser');
      dispatch(
        reduxActions.signIn({
          token: access_token,
          refreshToken: refresh_token,
          sessionId: session_id
        })
      );

      if (access_token) {
        showSuccessMessage(message || t('Użytkownik zalogowany poprawnie'));
      } else {
        showErrorMessage(message || t('Błędne dane użytkownika'));
      }

      if (status === 1) {
        navigate('/login');
        return;
      }

      if (access_token) {
        if (location.pathname.includes('login')) {
          navigate(returnUrl || `/${i18n.language}`);
          return;
        }
        navigate('/');
      } else {
        showErrorMessage(message || t('Błędne dane użytkownika'));
      }
    }
  });

  const { errors, handleSubmit, values, setFieldValue } = useFormik({
    initialValues: {
      username: localStorage.getItem('rememberUser') || '',
      password: ''
    },
    validationSchema: LoginFormSchema,
    validateOnChange: false,
    onSubmit: (values) => {
      login({
        ...values,
        client_id: REACT_APP_OPEN_PROFILE_CLIENT_ID || '',
        client_secret: REACT_APP_OPEN_PROFILE_CLIENT_SECRET || ''
      });
    }
  });

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        { [styles.popoverMode]: isPopover },
        'StylePath-Pages-Wise-WiseLogin'
      )}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('Logowanie')}</h1>
        {!isPopover && (
          <p className={styles.subtitle}>
            <Trans>Zaloguj się do swojego konta</Trans>
          </p>
        )}

        <form className={styles.form} onSubmit={handleSubmit}>
          <FormElement>
            <Input
              placeholder={t('Login')}
              value={values.username}
              onChange={(value) => setFieldValue('username', value)}
              error={errors.username}
            />
          </FormElement>
          <FormElement>
            <Input
              type="password"
              placeholder={t('Hasło')}
              value={values.password}
              onChange={(value) => setFieldValue('password', value)}
              error={errors.password}
            />
          </FormElement>

          <div className={styles.rowBetween}>
            <label className={styles.rememberMe}>
              <Checkbox checked={rememberMe} onClick={() => setRememberMe((prev) => !prev)} />
              <span>
                <Trans>Zapamiętaj mnie</Trans>
              </span>
            </label>
            <Link to="/forgot-password" className={styles.forgotLink}>
              <Trans>Odzyskiwanie hasła</Trans>
            </Link>
          </div>

          <div className={styles.submitButton}>
            <Button htmlType="submit" loading={isLogging}>
              <BoxArrowInRight className={styles.buttonIcon} />
              {t('ZALOGUJ')}
            </Button>
          </div>
        </form>

        <div className={styles.divider}>
          <span>{t('Lub')}</span>
        </div>

        <div className={styles.registerBlock}>
          <h2 className={styles.registerTitle}>
            <Trans>Nie masz jeszcze konta?</Trans>
          </h2>
          <p className={styles.registerSubtitle}>
            <Trans>Załóż konto i korzystaj z pełnej oferty B2B</Trans>
          </p>
          <div className={styles.registerButton}>
            <Button color="secondary" onClick={() => navigate('/register')}>
              <PersonPlus className={styles.buttonIcon} />
              {t('ZAŁÓŻ KONTO')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiseLogin;
